import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Typography, TextField, FormControlLabel,
  Checkbox, Autocomplete, Button, useTheme, InputBase,
} from "@mui/material";

import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import { requester } from "api/pesabackend";
import InvoiceDetailForm from "./invoiceDetailForm";
import InvoiceConfirmView from "../views/invoiceConfirmView";
import FixedSizeDialog from "components/fixedSizeDialog";
import CustomerForm from "scenes/customer/forms/customerForm";
import TopSnack from "components/TopSnack";

const invoiceSchema = yup.object().shape({
  invoice_products: yup.array()
    .of(yup.object().shape({
      related_product: yup.number().required("required"),
      related_product_unit: yup.number().required("required"),
      batch: yup.string().required("required"),
      quantity: yup.number().min(0.5, "Min 0.5").required("required"),
      discount: yup.number().required("required"),
    }))
    .required("Add at least one item")
    .min(1, "At least one item is required"),
});

const makeEmptyProduct = () => ({
  related_product: "", related_product_unit: "",
  batch: "", quantity: 1, sale_price: "",
  discount: 0, total: 0, data_id: uuidv4(),
});

const makeEmptyDetail = () => ({
  quantity: 1, sale_price: "", batch: "", discount: 0, total: 0,
  product_name: "", product_unit: "", related_product: "",
  related_product_unit: "", data_id: uuidv4(),
});

const invoiceFormInitialValues = {
  invoice_description: "",
  total_amount: 0,
  total_discount: 0,
  invoice_products: [],
};

export default function InvoiceForm() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const bodyBg = theme.palette.background.alt;
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_id = useSelector((s) => s.authReducer.business_id);
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const [open, setOpen] = useState(false);
  const [openNewCustomer, setOpenNewCustomer] = useState(false);
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  const [customer, setCustomer] = useState(0);
  const [hasCustomer, setHasCustomer] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [billDate, setBillDate] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [openSnack, setOpenSnack] = useState({ open: false, vertical: "top", horizontal: "center" });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const [firstName, setfirstName] = useState("");
  const [lastName, setLastName] = useState(".");
  const [email, setEmail] = useState("a@gmail.com");
  const [address, setAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

  const [invoiceDetailFormData, setInvoiceDetailFormData] = useState([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await requester.get(`/customers/list?search=False&business_branch=${business_branch_id}`);
      setCustomers(res.data.data);
    } catch { }
  }, [business_branch_id]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* search products */
  const handleSearchChange = async (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (q.length >= 2) {
      try {
        const res = await requester.get(
          `/inventory/products/list?business_branch=${business_branch_id}&search=${q}&typeFilter=all`
        );
        setSearchResults(res.data.data ?? []);
        setShowResults(true);
      } catch { setSearchResults([]); }
    } else {
      setShowResults(false);
      setSearchResults([]);
    }
  };

  const handleGrandTotal = (arr) => setGrandTotal(arr.reduce((t, i) => t + Number(i.total), 0));
  const handleTotalDiscount = (arr) => setTotalDiscount(arr.reduce((t, i) => t + Number(i.discount), 0));

  const handleItemAmountChange = (price, total, quantity, batch, product_name, product_unit, related_product, related_product_unit, index) => {
    const newArr = invoiceDetailFormData.map((item, idx) =>
      idx === index ? { ...item, sale_price: parseFloat(price), total: parseFloat(total), quantity: parseFloat(quantity), batch, product_unit, product_name, related_product, related_product_unit } : item
    );
    setInvoiceDetailFormData(newArr);
    handleGrandTotal(newArr);
  };

  const handleItemDiscountChange = (e, index) => {
    const newArr = invoiceDetailFormData.map((item, idx) =>
      idx === index ? { ...item, discount: e.target.value } : item
    );
    handleTotalDiscount(newArr);
    setInvoiceDetailFormData(newArr);
  };

  const handleRemoveProductDetailRow = (index) => {
    const newArr = [...invoiceDetailFormData];
    newArr.splice(index, 1);
    setInvoiceDetailFormData(newArr);
    handleGrandTotal(newArr);
    handleTotalDiscount(newArr);
  };

  const saveCustomer = async () => {
    setIsSubmittingCustomer(true);
    try {
      await requester.post("/customers/add", {
        related_business: business_id, related_business_branch: business_branch_id,
        first_name: firstName, last_name: lastName,
        email, address, phone_number: phoneNumber,
      });
      setOpenNewCustomer(false);
    } catch { }
    setIsSubmittingCustomer(false);
  };

  const saveInvoice = async () => {
    setIsSubmittingInvoice(true);
    try {
      await requester.post("/billing/invoices", {
        related_business: business_id,
        related_business_branch: business_branch_id,
        total_amount: grandTotal.toFixed(2),
        total_discount: totalDiscount.toFixed(2),
        related_customer: customer,
        date_created: billDate,
        invoice_products: invoiceDetailFormData,
      });
      navigate("/billing");
    } catch {
      setOpenSnack({ ...openSnack, open: true, message: "Please check the bill details", severity: "error" });
    }
    setIsSubmittingInvoice(false);
  };

  const inputSx = {
    "& .MuiInputBase-root": { fontSize: "13px", height: "34px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  return (
    <Box sx={{
      height: "100vh", display: "flex", flexDirection: "column",
      backgroundColor: bodyBg, overflow: "hidden",
    }}>

      {/* ══ FIXED TOP: header + bill details ══ */}
      <Box sx={{ flexShrink: 0, px: "1.5rem", pt: "1.25rem", pb: "1rem" }}>

        {/* Page title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
          <Box sx={{
            width: "36px", height: "36px", borderRadius: "9px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <ReceiptIcon sx={{ fontSize: "19px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "20px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Record sales bill
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "1px" }}>
              Fill in the details to create a new bill
            </Typography>
          </Box>
        </Box>

        {/* Bill meta bar */}
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px",
          px: "14px", py: "10px",
          backgroundColor: cardBg,
          border: `0.5px solid ${border}`,
          borderRadius: "10px",
        }}>
          {/* Date */}
          <Box>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec, mb: "4px" }}>
              Date of bill
            </Typography>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "6px",
              px: "10px", py: "5px",
              border: `0.5px solid ${border}`,
              borderRadius: "8px", fontSize: "13px", color: textPri,
              backgroundColor: bodyBg,
            }}>
              <ReceiptIcon sx={{ fontSize: "13px", opacity: 0.4 }} />
              <input
                type="datetime-local"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontSize: "13px", color: "inherit", fontFamily: "inherit" }}
              />
            </Box>
          </Box>

          <Box sx={{ width: "1px", height: "36px", backgroundColor: border }} />

          {/* Customer */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasCustomer}
                  onChange={(e) => setHasCustomer(e.target.checked)}
                  size="small"
                  sx={{ p: "4px", color: textSec, "&.Mui-checked": { color: accent } }}
                />
              }
              label={<Typography sx={{ fontSize: "13px", color: textPri }}>Attach customer to bill</Typography>}
              sx={{ m: 0 }}
            />
            {hasCustomer && (
              <>
                <Autocomplete
                  size="small"
                  onChange={(_, v) => setCustomer(v ? v.id : "")}
                  options={customers}
                  getOptionLabel={(o) => `${o.related_user.first_name} ${o.related_user.last_name}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} key={o.id}>
                      {o.related_user.first_name} {o.related_user.last_name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select a customer" sx={{ ...inputSx, width: "200px" }} />
                  )}
                  sx={{ width: "200px" }}
                />
                <Button
                  onClick={() => setOpenNewCustomer(true)}
                  startIcon={<PersonAddIcon sx={{ fontSize: "14px" }} />}
                  sx={{
                    fontSize: "12px", fontWeight: 500, px: "10px", py: "5px",
                    borderRadius: "8px", textTransform: "none",
                    border: `0.5px solid ${border}`,
                    color: accent, backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "rgba(29,158,117,0.07)" },
                  }}
                >
                  New Customer
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ══ SCROLLABLE MIDDLE: item cards ══ */}
      <Formik
        initialValues={invoiceFormInitialValues}
        validationSchema={invoiceSchema}
        validateOnMount validateOnChange={false} validateOnBlur={false}
      >
        {({ values, errors, touched, handleBlur, handleChange, validateForm, isValid, resetForm, setFieldValue }) => (
          <form style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            <FieldArray name="invoice_products" render={(arrayHelpers) => (
              <>
                {/* Scrollable item list */}
                <Box sx={{
                  flex: 1, overflowY: "auto", px: "1.5rem", py: "0", display: "flex", flexDirection: "column", gap: "8px",
                  pb: "8px",
                  "&::-webkit-scrollbar": { width: "3px" },
                  "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", borderRadius: "3px" },
                }}>
                  {values.invoice_products.length === 0 ? (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: "3rem", gap: "8px" }}>
                      <Box sx={{
                        width: "44px", height: "44px", borderRadius: "50%",
                        backgroundColor: "rgba(29,158,117,0.08)",
                        display: "flex", alignItems: "center", justifyContent: "center", color: accent,
                      }}>
                        <ReceiptIcon sx={{ fontSize: "20px" }} />
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: textSec }}>
                        No items yet — search a product below to add it
                      </Typography>
                    </Box>
                  ) : (
                    values.invoice_products.map((row, index) => (
                      <InvoiceDetailForm
                        key={row.data_id}
                        index={index}
                        data={invoiceDetailFormData[index] ?? row}
                        handleRemoveProductDetailRow={(i) => {
                          arrayHelpers.remove(i);
                          handleRemoveProductDetailRow(i);
                        }}
                        handleItemAmountChange={handleItemAmountChange}
                        handleItemDiscountChange={handleItemDiscountChange}
                        handleChange={handleChange}
                        errors={arrayHelpers.form.errors}
                        touched={arrayHelpers.form.touched}
                        name={`invoice_products[${index}]`}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                      />
                    ))
                  )}
                </Box>

                {/* ══ FIXED FOOTER ══ */}
                <Box sx={{
                  flexShrink: 0,
                  backgroundColor: cardBg,
                  borderTop: `0.5px solid ${border}`,
                }}>
                  {/* Search bar */}
                  <Box sx={{ px: "1.5rem", pt: "10px", pb: "6px", position: "relative" }} ref={searchRef}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: "8px",
                      px: "12px", py: "8px",
                      border: `1.5px solid ${showResults || searchQuery.length > 0 ? accent : border}`,
                      borderRadius: "10px",
                      backgroundColor: bodyBg,
                      transition: "border-color 0.15s",
                    }}>
                      <SearchIcon sx={{ fontSize: "16px", color: accent, flexShrink: 0 }} />
                      <InputBase
                        placeholder="Search product (min 2 chars)…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
                        onBlur={() => setTimeout(() => setShowResults(false), 150)}
                        sx={{ flex: 1, fontSize: "13px", color: textPri, "& input::placeholder": { color: textSec } }}
                      />
                    </Box>

                    {/* Dropdown results */}
                    {showResults && searchResults.length > 0 && (
                      <Box sx={{
                        position: "absolute", bottom: "100%", left: "1.5rem", right: "1.5rem",
                        backgroundColor: cardBg,
                        border: `0.5px solid ${border}`,
                        borderRadius: "10px",
                        mb: "4px",
                        maxHeight: "220px", overflowY: "auto",
                        zIndex: 100,
                        boxShadow: isDark ? "0 -8px 24px rgba(0,0,0,0.4)" : "0 -8px 24px rgba(0,0,0,0.1)",
                        "&::-webkit-scrollbar": { width: "3px" },
                        "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", borderRadius: "3px" },
                      }}>
                        {searchResults.map((product, i) => (
                          <Box
                            key={product.id}
                            onMouseDown={() => {
                              const newFormikRow = makeEmptyProduct();
                              newFormikRow.related_product = product.id;
                              arrayHelpers.push(newFormikRow);
                              setInvoiceDetailFormData(prev => [...prev, { ...makeEmptyDetail(), product_name: `${product.product_name} ${product.description}`, related_product: product.id, data_id: newFormikRow.data_id }]);
                              setSearchQuery("");
                              setShowResults(false);
                            }}
                            sx={{
                              display: "flex", alignItems: "center", gap: "10px",
                              px: "12px", py: "10px",
                              borderBottom: i < searchResults.length - 1 ? `0.5px solid ${border}` : "none",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: isDark ? "rgba(29,158,117,0.07)" : "rgba(29,158,117,0.04)" },
                              transition: "background 0.1s",
                            }}
                          >
                            <Box sx={{
                              width: "22px", height: "22px", borderRadius: "50%",
                              border: `1.5px solid ${accent}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: accent, flexShrink: 0,
                            }}>
                              <AddCircleOutlineIcon sx={{ fontSize: "14px" }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                                {product.product_name} {product.description}
                              </Typography>
                              <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>
                                {product.unit} · {product.selling_price ?? "—"}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Totals + action buttons */}
                  <Box sx={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", flexWrap: "wrap",
                    px: "1.5rem", pt: "6px", pb: "12px", gap: "12px",
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                      <Box>
                        <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
                          {values.invoice_products.length} items · discount
                        </Typography>
                        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#e24b4a" }}>
                          -{Number(totalDiscount).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ width: "1px", height: "28px", backgroundColor: border }} />
                      <Box>
                        <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
                          Grand total
                        </Typography>
                        <Typography sx={{ fontSize: "20px", fontWeight: 500, color: accent }}>
                          {Number(grandTotal).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Button
                        onClick={() => { resetForm(); navigate("/billing"); }}
                        startIcon={<CancelIcon sx={{ fontSize: "15px" }} />}
                        sx={{
                          fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem",
                          borderRadius: "8px", textTransform: "none",
                          backgroundColor: "#e24b4a", color: "#fff",
                          "&:hover": { backgroundColor: "#c23a39" },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => validateForm().then(() => { if (isValid) setOpen(true); })}
                        startIcon={<CheckCircleIcon sx={{ fontSize: "15px" }} />}
                        sx={{
                          fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem",
                          borderRadius: "8px", textTransform: "none",
                          backgroundColor: accent, color: "#fff",
                          "&:hover": { backgroundColor: "#0F6E56" },
                        }}
                      >
                        {!isValid ? "Verify Bill" : "Create Bill"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </>
            )} />

          </form>
        )}
      </Formik>

      {/* ── Dialogs ── */}
      <FixedSizeDialog
        open={open}
        handleClose={() => setOpen(false)}
        dialogHeading="Confirm Bill Details"
        positiveResponse="Confirm Bill"
        handleSubmit={saveInvoice}
        isSubmitting={isSubmittingInvoice}
        isSubmittingText="Submitting bill, please wait…"
        negativeResponse="Cancel"
        content={
          <InvoiceConfirmView
            invoiceDescription="New Bill"
            invoiceDetails={invoiceDetailFormData}
            total={grandTotal}
            discount={totalDiscount}
          />
        }
      />

      <FixedSizeDialog
        open={openNewCustomer}
        handleClose={() => setOpenNewCustomer(false)}
        dialogHeading="Add New Customer"
        positiveResponse="Create Customer"
        handleSubmit={saveCustomer}
        isSubmitting={isSubmittingCustomer}
        isSubmittingText="Creating customer…"
        negativeResponse="Cancel"
        content={
          <CustomerForm
            handleChangeFirstName={(e) => setfirstName(e.target.value)}
            handleChangeLastName={(e) => setLastName(e.target.value)}
            handleChangeEmailAddress={(e) => setEmail(e.target.value)}
            handleChangeAddress={(e) => setAddress(e.target.value)}
            handleChangePhoneNumber={(e) => setPhoneNumber(e.target.value)}
          />
        }
      />

      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={(_, reason) => { if (reason !== "clickaway") setOpenSnack({ ...openSnack, open: false }); }}
        message={openSnack.message}
        severity={openSnack.severity}
      />
    </Box>
  );
}